import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Phone, Mail, MessageCircle, Download, Globe } from 'lucide-react';
import { companyInfo } from '../mock';

const PEOPLE = {
  escaynor: {
    name: 'Escaynor Ng',
    position: 'Director',
    phoneRaw: '6598366186',
    phoneDisplay: '+65 9836 6186',
  },
  george: {
    name: 'George Ng',
    position: 'Executive Manager & Executive Sale Manager',
    phoneRaw: '6597151515',
    phoneDisplay: '+65 9715 1515',
  },
};

export default function ENameCard() {
  const { person } = useParams();
  const p = PEOPLE[(person || '').toLowerCase()];
  if (!p) return <Navigate to="/connect" replace />;

  const email = companyInfo.email;
  const waUrl = `https://wa.me/${p.phoneRaw}?text=${encodeURIComponent(`Hi ${p.name.split(' ')[0]}, I'd like to enquire about NJK Logistics services.`)}`;

  const downloadVCard = () => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${p.name}`,
      `ORG:${companyInfo.name}`,
      `TITLE:${p.position}`,
      `TEL;TYPE=CELL,VOICE:+${p.phoneRaw}`,
      `EMAIL;TYPE=WORK:${email}`,
      'URL:https://njklogisticsservices.com',
      `ADR;TYPE=WORK:;;${companyInfo.address};Singapore;;;Singapore`,
      `NOTE:${companyInfo.tagline}`,
      'END:VCARD',
    ].join('\n');
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${p.name.replace(/\s+/g, '_')}_NJK.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-burgundy-deep grain-overlay flex items-center justify-center p-5">
      <div className="w-full max-w-md relative z-10">
        <div className="bg-burgundy-dark rounded-2xl shadow-2xl shadow-black/50 overflow-hidden border border-gold/25" data-testid="ename-card">
          {/* Logo + company header */}
          <div className="px-6 pt-8 pb-6 text-center border-b border-gold/15">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-24 w-auto object-contain mx-auto mb-4" data-testid="ename-logo" />
            <p className="font-display text-gold text-xl font-bold tracking-tight">{companyInfo.name}</p>
            <p className="text-[10px] text-gold-light/60 uppercase tracking-[0.3em] mt-1.5">Swift · Secure · Reliable</p>
          </div>

          {/* Person */}
          <div className="px-6 py-7 text-center">
            <h1 className="font-display text-3xl font-bold text-white tracking-tight" data-testid="ename-person-name">{p.name}</h1>
            <p className="text-gold text-sm font-semibold uppercase tracking-[0.15em] mt-2 leading-relaxed" data-testid="ename-position">{p.position}</p>

            <div className="mt-7 space-y-3 text-left">
              <a href={`tel:+${p.phoneRaw}`} className="flex items-center gap-4 bg-burgundy/60 border border-gold/20 rounded-xl px-5 py-3.5 hover:border-gold/50 active:scale-[0.98] transition-all" data-testid="ename-phone">
                <Phone size={18} className="text-gold shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Mobile</p>
                  <p className="text-white font-semibold">{p.phoneDisplay}</p>
                </div>
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-4 bg-burgundy/60 border border-gold/20 rounded-xl px-5 py-3.5 hover:border-gold/50 active:scale-[0.98] transition-all" data-testid="ename-email">
                <Mail size={18} className="text-gold shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Email</p>
                  <p className="text-white font-semibold text-sm truncate">{email}</p>
                </div>
              </a>
              <a href="https://njklogisticsservices.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-burgundy/60 border border-gold/20 rounded-xl px-5 py-3.5 hover:border-gold/50 active:scale-[0.98] transition-all" data-testid="ename-website">
                <Globe size={18} className="text-gold shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Website</p>
                  <p className="text-white font-semibold text-sm">njklogisticsservices.com</p>
                </div>
              </a>
            </div>
          </div>

          <div className="bg-gold text-burgundy-deep text-center py-2.5 tracking-[0.25em] font-bold text-xs uppercase">
            Heavy Vehicle Logistics · Singapore
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
            data-testid="ename-whatsapp-btn">
            <MessageCircle size={20} /> WhatsApp
          </a>
          <button onClick={downloadVCard}
            className="bg-gold hover:bg-gold-light text-burgundy-deep font-bold py-4 rounded-xl shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
            data-testid="ename-save-btn">
            <Download size={20} /> Save Contact
          </button>
        </div>

        <p className="text-center text-xs text-white/40 mt-6">© {new Date().getFullYear()} {companyInfo.name}</p>
      </div>
    </div>
  );
}
